import './style.scss';

import { useQuery } from '@tanstack/react-query';
import React, { Fragment, useContext } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { Divider, Icon, Label, Popup } from 'semantic-ui-react';
import _ from 'lodash';

import { PACSCommunicationContext } from '../PACSCommunicationProvider/context';
import {
  PACSCommunication,
  PresentationContext,
  Result,
  resultToString,
} from 'app/interfaces/PACSCommunication';
import queryPACSAssociationStatusOptions, {
  addAllPresentationContextsToConfiguration,
} from 'app/queries/queryPACSAssociationStatusOptions';
import { PACSConfigurationState } from 'app/redux/PACSConfiguration/reducer';
import useMinDurationLoadingState from 'app/hooks/useMinDurationLoadingState';

const ASSOCIATION_STATUS_OPTION = {
  unknown: {
    id: 'pacs.association_status.unknown',
    icon: 'question',
    color: 'darkgray',
  },
  ongoing: {
    id: 'pacs.association_status.ongoing',
    icon: 'sync',
    color: 'blue',
  },
  connected: {
    id: 'pacs.association_status.connected',
    icon: 'check',
    color: 'green',
  },
  notConnected: {
    id: 'pacs.association_status.not_connected',
    icon: 'times',
    color: 'red',
  },
  partial: {
    id: 'pacs.association_status.partial',
    icon: 'warning',
    color: 'orange',
  },
  disabled: {
    id: 'pacs.association_status.disabled',
    icon: 'ban',
    color: 'red',
  },
} as const;

type PACSAssociationStatusProps = {
  pacsConfiguration?: PACSConfigurationState;
  isDisableActiveCheck?: boolean;
  pacsCommunication?: PACSCommunication;
};

type PACSAssociationStatusListProps = {
  negociatedPresentationContexts: {
    echo: PresentationContext;
    store: PresentationContext;
    findPatient: PresentationContext;
    findStudy: PresentationContext;
    findWorklist: PresentationContext;
    getStudy: PresentationContext;
  };
};

const camelCaseToCapitalizedWords = (str: string) => {
  return str.replace(/([a-z])([A-Z])/g, '$1 $2').replace(/^\w/, (c) => c.toUpperCase());
};

function PACSAssociationStatusList({
  negociatedPresentationContexts,
}: PACSAssociationStatusListProps) {
  const intl = useIntl();
  if (!negociatedPresentationContexts) {
    return null;
  }
  return (
    <div className="pacs-association-status__presentation-contexts-list">
      {_.map(negociatedPresentationContexts, ({ result }, presentationContextKey) => {
        const isPresentationContextFunctional = result === Result.Acceptance;

        return (
          <Fragment key={presentationContextKey}>
            <div
              style={{ justifySelf: 'end' }}
              title={intl.formatMessage({
                id: `pacs.association_status.presentation_contexts.${presentationContextKey}.usage`,
              })}
            >
              <Label color={isPresentationContextFunctional ? 'green' : 'red'}>
                {camelCaseToCapitalizedWords(presentationContextKey)}
              </Label>
            </div>
            <strong>
              <FormattedMessage id={`pacs.association_status.result.${resultToString(result)}`} />
            </strong>
          </Fragment>
        );
      })}
    </div>
  );
}

export default function PACSAssociationStatus(props: PACSAssociationStatusProps) {
  const { pacsConfiguration, isDisableActiveCheck } = props;
  const { enabled } = pacsConfiguration ?? {};

  const pacsCommunication = props?.pacsCommunication ?? useContext(PACSCommunicationContext);

  const { data, error, isFetching } = useQuery({
    ...queryPACSAssociationStatusOptions(
      addAllPresentationContextsToConfiguration(pacsConfiguration),
      { pacsCommunication }
    ),
    enabled: !isDisableActiveCheck && enabled === true,
    staleTime: 5 * 60 * 1000,
    gcTime: 5 * 60 * 1000,
  });
  let isLoading = useMinDurationLoadingState(isFetching, 1000);

  let options: typeof ASSOCIATION_STATUS_OPTION[keyof typeof ASSOCIATION_STATUS_OPTION] =
    ASSOCIATION_STATUS_OPTION.unknown;
  if (enabled === undefined) {
    options = ASSOCIATION_STATUS_OPTION.unknown;
    isLoading = false;
  } else if (enabled === false) {
    options = ASSOCIATION_STATUS_OPTION.disabled;
  } else if (isLoading) {
    options = ASSOCIATION_STATUS_OPTION.ongoing;
  } else {
    const isPACSAssociated = data?.presentation_contexts?.every(
      ({ result }) => result === Result.Acceptance
    );
    const isPACSPartialAssociated = data?.presentation_contexts?.some(
      ({ result }) => result === Result.Acceptance
    );
    if (isPACSAssociated) {
      options = ASSOCIATION_STATUS_OPTION.connected;
    } else if (isPACSPartialAssociated) {
      options = ASSOCIATION_STATUS_OPTION.partial;
    } else {
      options = ASSOCIATION_STATUS_OPTION.notConnected;
    }
  }

  const negociatedPresentationContexts = data && {
    echo: data.presentation_contexts[0],
    store: data.presentation_contexts[1],
    findPatient: data.presentation_contexts[2],
    findStudy: data.presentation_contexts[3],
    findWorklist: data.presentation_contexts[4],
    getStudy: data.presentation_contexts[5],
  };

  return (
    <Popup
      hoverable
      trigger={
        <div className="pacs-association-status">
          <div className={`pacs-association-status__title ${options.color}`}>
            <FormattedMessage id="pacs.association_status.status.title" />
          </div>
          <div className={`pacs-association-status__state ${options.color}`}>
            <Icon
              className="picoxia-fix"
              name={isLoading ? 'sync' : options.icon}
              loading={isLoading}
            />
            <FormattedMessage id={options.id} />
          </div>
        </div>
      }
      disabled={!data}
    >
      <Popup.Header>
        <FormattedMessage id="pacs.association_status.presentation_contexts.title" />
      </Popup.Header>
      <Divider />
      <Popup.Content>
        <PACSAssociationStatusList
          negociatedPresentationContexts={negociatedPresentationContexts}
        />
      </Popup.Content>
    </Popup>
  );
}
